import React, { useState, useEffect } from 'react'
import { Box, Dialog, DialogTitle } from '@material-ui/core'
import { CardElement } from '@stripe/react-stripe-js'
import { Form, Formik } from 'formik'
import { connect } from 'react-redux'
import { number, object, string } from 'yup'
import { isNil } from 'lodash'

import { setCurrentStepStart } from '../../../redux/modules/teamGiftCards'
import { removeActionableMember } from '../../../redux/modules/teamMembers'
import ButtonDialogClose from '../common/ButtonDialogClose'
import { selectTeam } from '../../../redux/selectors/teamSelectors'
import { loadTeamPaymentMethodsStart } from '../../../redux/modules/team'
import { enqueueSnackbar } from '../../../redux/modules/snackbar'
import { selectActionableMembers } from '../../../redux/selectors/teamMembersSelectors'
import useAddPaymentMethod from '../../../hooks/useAddPaymentMethod'
import {
  getViewByStep,
  publishClickedBack,
  publishClickedClose,
  publishClickedConfirmClose,
  publishClickedNext,
} from '../../../util/events/teamsGiftsEventPublishers'
import { VIEWS } from '../../../util/events/teamsEvents'
import StepGiftDetails from './StepGiftDetails'
import DottedProgress from './DottedProgress'
import StepGiftRecipients from './StepGiftRecipients'
import {
  getAmountPer,
  getStepDetails,
  GIFT_AMOUNT_OTHER,
  isDottedStep,
  STEPS,
  TOTAL_DOTTED_STEPS,
} from './giftCardUtils'
import StepGiftPayment from './StepGiftPayment'
import ExitConfirmation from './ExitConfirmation'
import StepAddPaymentMethod from './StepAddPaymentMethod'

const validationSchema = object().shape({
  reason: string().required('Please select a reason.'),
  amount: string().required('Please select a gift amount.'),
  otherAmount: number().test(
    'otherAmount',
    'Please enter a gift amount greater than zero.',
    function(val) {
      const { amount } = this.options.parent
      if (amount !== GIFT_AMOUNT_OTHER) {
        return true
      }
      if (!val || val <= 0) {
        return false
      }
      return true
    },
  ),
})

const DialogSendGifts = ({
  team,
  actionableMembers,
  currentStep,
  isCreatingGiftCards,
  errorCreatingGiftCards,
  paymentMethods,
  removeActionableMember,
  setCurrentStepStart,
  loadTeamPaymentMethodsStart,
  onClose,
}) => {
  const [showExitConfirmation, setShowExitConfirmation] = useState(false)
  const { isCreatingPaymentMethod, handleAddPaymentMethod } = useAddPaymentMethod({
    ownerType: 'ENTERPRISE',
    referenceId: team.id,
    onSuccess: () => {
      setTimeout(() => loadTeamPaymentMethodsStart(team.id), 1200)

      setCurrentStepStart(nextStep)
    },
  })
  const hasDefaultPaymentMethod = paymentMethods.some(pm => pm.default)
  const { step, title, nextStep, previousStep } = getStepDetails(
    currentStep,
    hasDefaultPaymentMethod,
    actionableMembers.length,
  )
  const view = !isNil(currentStep) && getViewByStep(currentStep)

  const handleSubmit = values => {
    if (currentStep === STEPS.GIFT_ADD_PAYMENT) {
      handleAddPaymentMethod()
      return
    }

    if (currentStep === STEPS.GIFT_PAYMENT) {
      handleCompletePurchase(values)
      return
    }

    publishClickedNext(view)
    setCurrentStepStart(nextStep)
  }

  const handleBack = () => {
    setCurrentStepStart(previousStep)
    publishClickedBack(view)
  }

  const handleClose = () => {
    publishClickedClose(view)
    setShowExitConfirmation(true)
  }

  const onBackFromExit = () => {
    publishClickedBack(VIEWS.CLOSE_GIFT_DIALOG)
    setShowExitConfirmation(false)
  }

  const handleConfirmClose = () => {
    publishClickedConfirmClose(view)
    onClose()
  }

  const handleCompletePurchase = values => {
    const { amount, otherAmount, reason, message } = values
    const amountPer = getAmountPer(amount, otherAmount)
    const userIds = actionableMembers.map(m => m.userId)
    setCurrentStepStart(
      nextStep,
      {
        userIds,
        amountPer,
        reason,
        message,
      },
      onClose,
    )
  }

  useEffect(() => {
    if (team.id) {
      setCurrentStepStart(STEPS.GIFT_DETAILS)
      loadTeamPaymentMethodsStart(team.id)
    }
  }, [loadTeamPaymentMethodsStart, setCurrentStepStart, team.id])

  return (
    <Dialog open fullWidth maxWidth="sm">
      <Formik
        initialValues={{
          amount: '',
          otherAmount: 0,
          reason: '',
          message: '',
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {props => (
          <Form>
            {showExitConfirmation && (
              <ExitConfirmation onBack={onBackFromExit} onConfirm={handleConfirmClose} />
            )}

            {!showExitConfirmation && (
              <>
                {isDottedStep(currentStep) && (
                  <Box display="flex" justifyContent="center" marginTop={3}>
                    <DottedProgress currentStep={step} totalSteps={TOTAL_DOTTED_STEPS} />
                  </Box>
                )}

                <DialogTitle>
                  {title}
                  <ButtonDialogClose onClick={handleClose} />
                </DialogTitle>

                {currentStep === STEPS.GIFT_DETAILS && <StepGiftDetails {...props} />}
                {currentStep === STEPS.GIFT_RECIPIENTS && (
                  <StepGiftRecipients
                    actionableMembers={actionableMembers}
                    onBack={handleBack}
                    onRemove={removeActionableMember}
                    {...props}
                  />
                )}
                {currentStep === STEPS.GIFT_ADD_PAYMENT && (
                  <StepAddPaymentMethod
                    team={team}
                    CardElement={CardElement}
                    submitting={isCreatingPaymentMethod}
                    onBack={handleBack}
                  />
                )}
                {currentStep === STEPS.GIFT_PAYMENT && (
                  <StepGiftPayment
                    actionableMembers={actionableMembers}
                    submitting={isCreatingGiftCards}
                    onBack={handleBack}
                    error={errorCreatingGiftCards}
                    {...props}
                  />
                )}
              </>
            )}
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

const mapStateToProps = state => {
  const team = selectTeam(state)
  const actionableMembers = selectActionableMembers(state)
  const { currentStep, isCreatingGiftCards, errorCreatingGiftCards } = state.teamGiftCards
  const { paymentMethods } = state.team

  return {
    team,
    actionableMembers,
    currentStep,
    isCreatingGiftCards,
    errorCreatingGiftCards,
    paymentMethods,
  }
}

const mapDispatchToProps = {
  removeActionableMember,
  setCurrentStepStart,
  loadTeamPaymentMethodsStart,
  enqueueSnackbar,
}

export default connect(mapStateToProps, mapDispatchToProps)(DialogSendGifts)
